import { useState } from 'react';
import type { DiagnosedEnglishState, ExploreCefrLevel } from '../../lib/englishApi.js';
import { useViewNavigate } from '../../hooks/useViewNavigate.js';
import { useDeferredFlag } from '../../hooks/useDeferredFlag.js';
import { useFontsSettled } from '../../hooks/useFontsSettled.js';
import { AppLoader } from '../../components/loading/AppLoader.js';
import { WalletButton } from '../../billing/WalletButton.js';
import { useCredits } from '../../billing/useCredits.js';
import { HelpButton } from '../../components/cefr-cycle/CefrCycleWidget.js';
import { TabBar } from '../../components/nav/TabBar.js';
import { HeaderLevelRing } from './parts/HeaderLevelRing.js';
import { LevelExploreSheet } from './parts/LevelExploreSheet.js';
import { ExploreBadge } from './parts/ExploreBadge.js';
import { ConverseHeroCard, ListenHeroCard, ProgramHeroCard } from './parts/HeroCards.js';
import { useGlobalListenBarActive } from '../listen/GlobalListenBar.js';
import { DailyReviewCard } from './parts/DailyReviewCard.js';
import { ProgressSubHeader, useRadarCount } from './parts/ProgressSubHeader.js';
import { useHomeResume } from './parts/ResumeHero.js';
import { CanDoHeroCard } from './parts/CanDoHeroCard.js';
import { useCanDoNext } from '../program/can-do/useCanDo.js';
import { useHomeStats } from './parts/StatRow.js';
import { useRoutine } from '../../lib/routine.js';
import { StreakChip } from './parts/StreakFlame.js';
import { StreakSheet } from './parts/StreakSheet.js';
import { useShipHome } from './ship/useShipHome.js';
import './home.css';
import './home-v2.css';

/**
 * Home v2: 3 tarjetas-hero (Conversar · Escuchar · Programa; reparto de alto
 * ponderado 4/3/3 con Conversar al frente) + sub-header "hoy" de una línea
 * (minutos + estado del día → /progress). La racha vive sólo en el StreakChip
 * del header; la cobertura de vocabulario, en Biblioteca. "Para vos" es el
 * pie de la card Programa.
 */
interface HomeMobileProps {
  /** Estado con nivel GARANTIZADO: el gate de `HomeScreen` ya filtró al user sin diagnóstico. `null` = todavía cargando. */
  level: DiagnosedEnglishState | null;
  onStartExplore: (level: ExploreCefrLevel) => Promise<void>;
  onStopExplore: () => Promise<void>;
  onSelfSet: (level: ExploreCefrLevel, applyRecommended?: boolean) => Promise<void>;
}

export function HomeMobile({ level, onStartExplore, onStopExplore, onSelfSet }: HomeMobileProps) {
  const navigate = useViewNavigate();
  const {
    balance,
    loading: balanceLoading,
    error: balanceError,
    refresh: refreshBalance,
  } = useCredits();
  const resume = useHomeResume();
  // The can-do path (plan 46): its next step feeds the Program card while
  // `canDoPath` is on; `off` (the API's explicit answer) keeps today's card.
  const canDoNext = useCanDoNext();
  const stats = useHomeStats();
  const { routine } = useRoutine();
  const listenBarActive = useGlobalListenBarActive();
  const radarCount = useRadarCount();
  const [streakOpen, setStreakOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);

  // Hold-and-reveal: esperamos a tener los datos críticos (nivel + resume
  // asentado) y recién ahí mostramos el home COMPLETO con un fade único, en
  // vez de que las filas "siguiendo" / "unidad en curso" aparezcan después y
  // empujen el resto. El chrome (TabBar) queda fijo; sólo revela el contenido.
  // `fontsSettled` entra al gate para que el contenido se mida UNA vez: sin
  // esto, el swap de Geist/Caveat post-reveal encogía las cards un toque.
  const fontsSettled = useFontsSettled();
  // «Armá tu nave» (encargo 117): presentación, la nave en el cuerpo y la línea «en viaje». Entra al
  // gate del hold-and-reveal: la nave no puede aparecer después y empujar el Inicio.
  const ship = useShipHome({
    variant: 'mobile',
    cefr: level?.cefrGlobal ?? null,
    // Con el Programa «Puedo…» prendido, la lección es el paso que le toca (el de su tarjeta).
    programUnitId:
      canDoNext.status === 'on'
        ? canDoNext.data.next.kind === 'step'
          ? canDoNext.data.next.unitId
          : null
        : ((resume.unit ?? resume.nextUnit)?.id ?? null),
  });
  const ready =
    level !== null &&
    resume.resolved &&
    fontsSettled &&
    canDoNext.status !== 'loading' &&
    ship.resolved;
  const showLoader = useDeferredFlag(!ready, 140);
  // Animamos el reveal SÓLO si la primera pintura fue el shell (hubo "hold").
  // Si los datos ya estaban en el caché de sesión, el cross-fade de rutas
  // (useViewNavigate) cubre la entrada — duplicarlo con `home-enter` apilaba
  // dos fades + dos slides de 8px con duraciones distintas.
  const [heldAtMount] = useState(!ready);
  const enterClass = heldAtMount ? ' home-enter' : '';

  const openConversation = () => navigate('/conversation');
  // Con categoría (tile del card Conversar) el hub abre filtrado en ese tab.
  const openScenarios = (category?: string) =>
    navigate('/scenarios', category ? { state: { category } } : undefined);
  // La unidad que muestra la card Programa (en curso, o la próxima sugerida).
  const programUnit = resume.unit ?? resume.nextUnit;
  // Con unidad concreta, el Programa abre con su popover de sub-unidades ya
  // desplegado (state.openUnitId), como si la hubieras tocado en el sendero.
  const openProgramUnit = () =>
    navigate('/program', programUnit ? { state: { openUnitId: programUnit.id } } : undefined);
  // Footer "Ver todo el programa" → la sección general, sin popover.
  const openProgram = () => navigate('/program');
  // Can-do card: the next step's unit panel (or the path itself).
  const openCanDoUnit = (unitId: string | null) =>
    navigate('/program', unitId ? { state: { openUnitId: unitId } } : undefined);
  const openListen = () => navigate('/listen');
  // Carga el capítulo si vino del backend (no estaba en sesión), luego navega.
  const openPlayer = () => {
    resume.startListen();
    void navigate('/listen/player');
  };

  // Mientras carga: chrome fijo (TabBar) + loader de marca (diferido para no
  // parpadear en cargas rápidas). El contenido real entra de una sola vez abajo.
  if (!ready) {
    return (
      <div className="app">
        {/* El TabBar baja solo al fondo (margin-top:auto en .tabbar) aunque el
            loader sea position:fixed y no haya contenido en flujo. */}
        {showLoader && <AppLoader page />}
        <TabBar />
      </div>
    );
  }

  return (
    <div className="app">
      {/* Header: anillo de nivel a la izquierda; saldo + racha a la derecha.
          `home-enter` va en el header y el scroll (el CONTENIDO), nunca en el
          root: si animara el root, el TabBar ya visible parpadearía. */}
      <header className={`home-header${enterClass}`}>
        <div className="home-header__row">
          <div className="home-header__lead">
            {level && (
              <HeaderLevelRing
                levelId={level.cefrGlobal}
                size={46}
                onOpen={() => setExploreOpen(true)}
                exploring={level.levelOverlay != null}
              />
            )}
          </div>
          <div className="home-header__trailing">
            <WalletButton
              balance={balance}
              loading={balanceLoading}
              error={balanceError}
              onRetry={refreshBalance}
              size="compact"
            />
            {/* Racha del backend: cargando → skeleton; con dato → chip; error
                → nada (no se inventa un 0). El loading va PRIMERO: cubre
                también la carga de sesiones — si totals resolviera antes, el
                chip flashearía 'is-pending' con todayDone aún sin derivar. */}
            {stats.loading ? (
              <span className="streak-chip is-skeleton" aria-hidden="true" />
            ) : stats.totals ? (
              <StreakChip
                count={stats.totals.currentStreak}
                todayDone={stats.todayDone}
                onClick={() => setStreakOpen(true)}
              />
            ) : null}
            <HelpButton />
          </div>
        </div>

        {/* Sub-header "hoy": minutos de hoy + estado del día, una línea, un
            destino (/progress). La racha vive SOLO en el StreakChip del header
            (antes estaba dos veces a 40px con destinos distintos) y la
            cobertura de vocabulario quedó en su casa: Biblioteca. */}
        {ship.subhead ?? (
          <ProgressSubHeader
            minutesToday={stats.minutesToday}
            todayDone={stats.todayDone}
            onOpen={() => navigate('/progress')}
            radarCount={radarCount}
            onOpenRadar={() => navigate('/progress/radar')}
            routine={routine ?? null}
            now={stats.now}
          />
        )}

        {level?.levelOverlay && (
          <ExploreBadge
            exploring={level.levelOverlay.level}
            realLevel={level.cefrGlobal}
            onOpen={() => setExploreOpen(true)}
          />
        )}
      </header>

      {/* Cuerpo — 3 héroes compactos. La entrada a Órbitas que iba arriba se
          sacó (card #525): Órbitas quedó apagada, ver la ruta en App.tsx. */}
      <div className={`app-scroll h2-body${enterClass}`}>
        {ship.body ?? (
          <>
            {/* Repaso del día: las correcciones que ya tocan (sólo si hay). */}
            <DailyReviewCard />
            <ConverseHeroCard onStart={openConversation} onScenarios={openScenarios} />
            <ListenHeroCard resume={resume} onOpenPlayer={openPlayer} onExplore={openListen} />
            {canDoNext.status === 'off' ? (
              <ProgramHeroCard
                unit={programUnit}
                onOpen={openProgramUnit}
                onExplore={openProgram}
              />
            ) : (
              <CanDoHeroCard state={canDoNext} onOpen={openCanDoUnit} onExplore={openProgram} />
            )}
          </>
        )}

        {/* Con la barra flotante global en pantalla el spacer crece para que no
            tape el último CTA de la card de Programa. */}
        <div className={`home-body__spacer${listenBarActive ? ' is-above-listen-bar' : ''}`} />
      </div>

      {streakOpen && stats.totals && (
        <StreakSheet
          calendar={stats.calendar}
          now={stats.now}
          current={stats.totals.currentStreak}
          longest={stats.totals.bestStreak}
          todayDone={stats.todayDone}
          minutesToday={stats.minutesToday}
          routine={routine ?? null}
          onEditRoutine={() => navigate('/profile/routine?desde=inicio')}
          onClose={() => setStreakOpen(false)}
          onPractice={openConversation}
        />
      )}

      {exploreOpen && level && (
        <LevelExploreSheet
          realLevel={level.cefrGlobal}
          overlay={level.levelOverlay}
          onExplore={onStartExplore}
          onStop={onStopExplore}
          onSelfSet={onSelfSet}
          onClose={() => setExploreOpen(false)}
        />
      )}

      {ship.intro}
      {ship.notice}

      <TabBar />
    </div>
  );
}
